import type {
  FetchServiceProviderListParams,
  ServiceProvider
} from 'Services/ServiceProviderManagement/Features/ServiceProvider/types'
import type { ListResponse } from 'Services/ServiceProviderManagement/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'

enum TagTypes {
  SERVICE_PROVIDER = 'ServiceProvider'
}

export const serviceProviderApi = createBaseApi({
  reducerPath: 'serviceProviderApi',
  keepUnusedDataFor: 5,
  tagTypes: [TagTypes.SERVICE_PROVIDER]
}).injectEndpoints({
  endpoints: builder => ({
    getServiceProviders: builder.query<
      ListResponse<ServiceProvider>,
      // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
      FetchServiceProviderListParams | void
    >({
      query: params => ({
        url: '/api/v1/service-provider/',
        params: params ?? undefined
      }),
      providesTags: result =>
        result
          ? [
              ...result.resources.map(
                ({ service_provider_id, pta }) =>
                  ({
                    type: TagTypes.SERVICE_PROVIDER,
                    id: `${pta}/${service_provider_id}`
                  }) as const
              ),
              { type: TagTypes.SERVICE_PROVIDER, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.SERVICE_PROVIDER, id: RTKCacheTags.LIST }]
    }),
    getAllServiceProviders: builder.query<
      ListResponse<ServiceProvider>,
      // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
      FetchServiceProviderListParams | void
    >({
      query: (params = {}) => ({
        url: '/api/v1/service-provider/',
        params: {
          ...params,
          bypass_pagination: true
        }
      }),
      providesTags: result =>
        result
          ? [
              ...result.resources.map(
                ({ service_provider_id, pta }) =>
                  ({
                    type: TagTypes.SERVICE_PROVIDER,
                    id: `${pta}/${service_provider_id}`
                  }) as const
              ),
              { type: TagTypes.SERVICE_PROVIDER, id: RTKCacheTags.LIST_ALL }
            ]
          : [{ type: TagTypes.SERVICE_PROVIDER, id: RTKCacheTags.LIST_ALL }]
    }),
    getSingleServiceProvider: builder.query<
      ServiceProvider,
      { serviceProviderId: string; pta: string }
    >({
      query: ({ serviceProviderId, pta }) =>
        `/api/v1/service-provider/${pta}/${serviceProviderId}/`,
      providesTags: (_result, _error, { serviceProviderId, pta }) => [
        { type: TagTypes.SERVICE_PROVIDER, id: `${pta}/${serviceProviderId}` }
      ]
    }),
    createSingleServiceProvider: builder.mutation<ServiceProvider, Partial<ServiceProvider>>({
      query: data => ({
        url: `/api/v1/service-provider/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [
        { type: TagTypes.SERVICE_PROVIDER, id: RTKCacheTags.LIST },
        { type: TagTypes.SERVICE_PROVIDER, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    updateSingleServiceProvider: builder.mutation<
      ServiceProvider,
      { serviceProviderId: string } & Partial<ServiceProvider>
    >({
      query: data => {
        const { serviceProviderId, ...fields } = data
        const updatedFields = {
          ...fields,
          // activity_end_date has to be null when updating passive service provider
          activity_end_date:
            !fields.active && fields.activity_end_date ? null : fields.activity_end_date
        }
        return {
          url: `/api/v1/service-provider/${fields.pta}/${serviceProviderId}/`,
          method: 'PUT',
          body: updatedFields
        }
      },
      invalidatesTags: (_result, _error, { serviceProviderId, pta }) => [
        { type: TagTypes.SERVICE_PROVIDER, id: `${pta}/${serviceProviderId}` },
        { type: TagTypes.SERVICE_PROVIDER, id: RTKCacheTags.LIST },
        { type: TagTypes.SERVICE_PROVIDER, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    deleteSingleServiceProvider: builder.mutation<
      Record<string, never>,
      { serviceProviderId: string; pta: string }
    >({
      query: ({ serviceProviderId, pta }) => ({
        url: `/api/v1/service-provider/${pta}/${serviceProviderId}/`,
        method: 'DELETE'
      }),
      invalidatesTags: [
        { type: TagTypes.SERVICE_PROVIDER, id: RTKCacheTags.LIST },
        { type: TagTypes.SERVICE_PROVIDER, id: RTKCacheTags.LIST_ALL }
      ]
    })
  })
})

export const {
  useGetServiceProvidersQuery,
  useGetAllServiceProvidersQuery,
  useGetSingleServiceProviderQuery,
  useCreateSingleServiceProviderMutation,
  useUpdateSingleServiceProviderMutation,
  useDeleteSingleServiceProviderMutation
} = serviceProviderApi
